import { prettyEnglishDate } from "@app/date";
import { formatCurrency } from "@app/deploy/cost";
import cn from "classnames";
import type React from "react";
import { useCallback } from "react";
import { ProgressBar as AriaProgressBar } from "react-aria-components";
import { LoadingSpinner } from "./loading";

export type ProgressBarVariant =
  | "default"
  | "info"
  | "success"
  | "warning"
  | "error";

export interface ProgressBarProps {
  value: number;
  maxValue: number;
  className?: string;
  variant?: ProgressBarVariant;
  height?: "sm" | "md" | "lg";
}

const HEIGHT_CLASSES = {
  sm: "h-1",
  md: "h-2",
  lg: "h-3",
};

// The status colors Banner uses for its icons. Text is a shade darker than the
// fill so an icon beside the bar keeps its contrast on a light background.
const VARIANT_CLASSES: Record<
  ProgressBarVariant,
  { fill: string; text: string }
> = {
  default: { fill: "bg-indigo", text: "text-indigo-500" },
  info: { fill: "bg-black-400", text: "text-black-500" },
  success: { fill: "bg-green-400", text: "text-green-500" },
  warning: { fill: "bg-orange-300", text: "text-orange-400" },
  error: { fill: "bg-red-400", text: "text-red-500" },
};

/** A text color for icons that sit beside the bar. */
export const progressBarTextColor = (variant: ProgressBarVariant) =>
  VARIANT_CLASSES[variant].text;

export const ProgressBar = ({
  value,
  maxValue,
  className = "",
  variant = "default",
  height = "md",
}: ProgressBarProps) => (
  <AriaProgressBar
    value={value}
    maxValue={maxValue}
    className={cn("border-none", className)}
    aria-label="Progress"
  >
    {({ percentage }) => (
      <div
        className={cn(
          HEIGHT_CLASSES[height],
          "border border-black-200 bg-black-50 rounded-sm overflow-hidden",
        )}
      >
        <div
          className={cn(
            "h-full transition-all duration-300 rounded-sm",
            VARIANT_CLASSES[variant].fill,
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    )}
  </AriaProgressBar>
);

export interface ProgressBarDetailProps {
  label: React.ReactNode;
  value: number;
  maxValue: number;
  unit?: string;
  resetDate?: string;
  variant?: ProgressBarVariant;
  className?: string;
  isLoading?: boolean;
}

export const ProgressBarDetail = ({
  label,
  value,
  maxValue,
  unit = "USD",
  resetDate,
  variant = "default",
  className = "",
  isLoading = false,
}: ProgressBarDetailProps) => {
  const formatValue = useCallback(
    (value: number) => {
      if (unit === "USD") {
        return formatCurrency(value);
      }
      return `${value} ${unit}`;
    },
    [unit],
  );

  return (
    <div className={className}>
      <h3 className="text-base font-semibold text-gray-900 mb-2">
        {label}
        <LoadingSpinner
          variant="xs"
          className="inline-block ml-2"
          show={isLoading}
        />
      </h3>
      <div className="text-2xl mb-2">{formatValue(maxValue)}</div>

      <ProgressBar
        value={value}
        maxValue={maxValue}
        variant={variant}
        className="mb-2"
      />

      <div className="flex items-center justify-between text-sm text-gray-500">
        <div>{formatValue(value)} used</div>
        {resetDate && <div>Resets on {prettyEnglishDate(resetDate)}</div>}
      </div>
    </div>
  );
};
